<template>
    <v-card rounded :elevation="elevation" :outlined="outlined" :class="classList">
        <v-card-title class="py-2 pr-2" style="flex-wrap: nowrap">
            <slot name="prepend" />
            <p class="mb-0 mx-2 clamp-1" style="font-size: 1rem">
                <span class="app-form-card__name">
                    {{ nameText }}
                </span>
                <span v-if="inputValue.$deleted" class="font-italic text--secondary px-4" style="font-size: 0.8rem">
                    {{ $t('removed') }}
                </span>
            </p>
            <v-spacer></v-spacer>
            <app-button
                v-if="!hideEditButton && !readonly && !isEditing && !deleted"
                :icon="getIcon('edit')"
                small
                :tooltip="$t('show_detail')"
                class="pa-1 pt-0"
                @click="$emit('input:editing', !isEditing)"
            />
            <app-button
                v-if="!hideRemoveButton && !readonly && !deleted && (!isEditing || !inputValue.is_valid)"
                :disabled="disableRemoveButton"
                :icon="getIcon('delete')"
                small
                :tooltip="$t('remove_this_item')"
                class="pa-1 pt-0"
                @click="onRemove"
            />
            <app-button
                v-if="!hideUndoButton && deleted"
                :icon="getIcon('undo')"
                small
                :tooltip="$t('cancel_removing_this_item')"
                class="pa-1 pt-0"
                @click="onUndoRemove"
            />
            <slot name="actions" />
        </v-card-title>
        <div v-if="!deleted" v-show="isEditing">
            <v-card-text>
                <div class="pa-4 pb-0">
                    <slot ref="slot" />
                </div>
            </v-card-text>
            <v-layout class="app-form__actions primaryLight mt-4 py-4 px-5 rounded justify-end">
                <app-button
                    :text="$t('hide_detail')"
                    :icon="getIcon('collapse', 'fad')"
                    secondary
                    small
                    :disabled="!inputValue.is_valid"
                    @click="isEditing = false"
                />
            </v-layout>
        </div>
    </v-card>
</template>

<script>
import { BaseModel } from '@/models';

export default {
    name: 'AppFormCard',
    props: {
        value: {
            type: Object,
            required: true,
            validator: v => {
                if (v === null) {
                    return true;
                }

                return v instanceof BaseModel;
            }
        },
        readonly: {
            type: Boolean,
            default: false
        },
        deleted: {
            type: Boolean,
            default: false
        },
        hideEditButton: {
            type: Boolean,
            default: false
        },
        hideRemoveButton: {
            type: Boolean,
            default: false
        },
        hideUndoButton: {
            type: Boolean,
            default: false
        },
        disableRemoveButton: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        inputValue: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        isEditing: {
            get() {
                if (this.readonly || this.deleted) {
                    return false;
                }
                return this.inputValue.$editing;
            },
            set(value) {
                if (this.readonly || this.deleted) {
                    this.$emit('input:editing', false);
                }
                this.$emit('input:editing', value);
            }
        },
        nameText() {
            return this.inputValue && typeof this.inputValue === 'object' && 'computed_name' in this.inputValue
                ? this.inputValue.computed_name
                : this.$t('item');
        },
        elevation() {
            if (this.readonly) {
                return 0;
            }

            if (this.deleted) {
                return 1;
            }

            return 2;
        },
        outlined() {
            if (this.readonly) {
                return true;
            }

            return false;
        },
        classList() {
            let classList = ['app-form-card'];

            if (this.deleted) {
                classList.push('app-form-card--deleted');
            }

            if (this.readonly) {
                classList.push('app-form-card--readonly');
            }

            return classList.join(' ');
        }
    },
    methods: {
        onRemove() {
            if (this.deleted || this.readonly || this.disableRemoveButton) {
                return;
            }

            this.$emit('input:remove', this.inputValue);
        },
        onUndoRemove() {
            if (!this.deleted) {
                return;
            }

            this.$emit('input:undo-remove', this.inputValue);
        }
    }
};
</script>

<style lang="scss">
.app-form-card {
    &__name {
        color: var(--v-primaryText-base);
    }
    &--deleted {
        .app-form-card__name {
            // color: var(--v-error-darken1);
            text-decoration: line-through;
        }
    }
    &--readonly {
        .app-form-card__name {
            color: rgba(0, 0, 0, 0.38);
        }
    }

    &:not(.app-form-card--deleted):has(.error--text) {
        border: 2px solid var(--v-error-base);
        .app-form-card__name {
            color: var(--v-error-base);
        }
    }
}
</style>
